import { TreeViewResponseItem } from "@/apis/preview/v2/types";
import { TooltipWrapper } from "@/components/common/tooltip";
import { cn } from "@/lib/utils";
import { TagType } from "@/types/mapping";
import { Columns3, FileText, Frame, Rows3Icon, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AxisIcon, LineItemIcon } from "../../icons";
import { DepthIcon } from "../tree/depth-icon";
import { TreeTagDetailPopover } from "./tree-tag-detail-popover";

// [매핑] 목록의 태그 상세 팝오버와 같은 딜레이.
const DETAIL_HOVER_DELAY_MS = 100;

export const DimensionIcon = ({
  tagType,
  isSum,
  isExtendedTag,
  isDisabled = false,
  colored = true,
}: {
  tagType: TagType;
  isSum: boolean;
  isExtendedTag: boolean;
  isDisabled?: boolean;
  colored?: boolean;
}) => {
  switch (tagType) {
    case "LINE_ITEMS":
      return (
        <Rows3Icon
          className={cn(
            "w-[32px] h-[32px] text-[#0A0A0A] p-[8px] flex items-center flex-shrink-0 bg-blue-100 rounded-[8px]",
            !colored && "bg-slate-200 text-[#0A0A0A]",
          )}
        />
      );
    case "LINE_ITEM":
      return (
        <LineItemIcon
          className={cn(
            "w-[32px] h-[32px] text-[#0A0A0A] p-[8px] flex items-center flex-shrink-0 bg-blue-100 rounded-[8px]",
            !colored && "bg-slate-200",
            colored &&
              isSum &&
              "bg-orange-100 border border-orange-200 text-orange-600",
            isDisabled && "bg-blue-50 text-neutral-400",
          )}
          isExtendedTag={isExtendedTag}
        />
      );
    case "AXIS":
      return (
        <Columns3
          className={cn(
            "w-[32px] h-[32px] text-[#0A0A0A] p-[8px] flex items-center flex-shrink-0 bg-blue-100 rounded-[8px]",
            !colored && "bg-slate-200",
          )}
        />
      );
    case "MEMBER":
      return (
        <AxisIcon
          className={cn(
            "w-[32px] h-[32px] text-[black] p-[8px] flex items-center flex-shrink-0 bg-blue-100 rounded-[8px]",
            !colored && "bg-slate-200",
            colored && isSum && "bg-blue-500",
          )}
          isExtendedTag={isExtendedTag}
        />
      );
    case "ABSTRACT":
      return (
        <Frame
          className={cn(
            "w-[32px] h-[32px] text-[white] p-[8px] flex items-center flex-shrink-0 bg-blue-500 rounded-[8px] ",
            !colored && "bg-slate-200 text-[#0A0A0A]",
            colored && isSum && "bg-blue-500",
          )}
        />
      );
    case "TEXTBLOCK":
      return (
        <FileText
          className={cn(
            "w-[32px] h-[32px] text-[#0A0A0A] p-[8px] flex items-center flex-shrink-0 bg-green-100 rounded-[8px]",
            !colored && "bg-slate-200",
            colored && isSum && "bg-green-200",
          )}
        />
      );
  }
};

const TreeTagItem = ({
  item,
  disabled = false,
  depth = 0,
  colored = true,
  isSelected = false,
  onClick,
  onHoverStart,
  onHoverEnd,
  onRemove,
  tagDetailRoleKey,
  className,
}: {
  item: TreeViewResponseItem;
  disabled?: boolean;
  depth?: number;
  colored?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
  /** 주어지면 행 hover 시 휴지통 버튼이 드러난다. */
  onRemove?: () => void;
  /** 주어지면 행 hover 시 "태그 상세정보" 팝오버를 띄운다. */
  tagDetailRoleKey?: string;
  className?: string;
}) => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const detailTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDetailTimer = () => {
    if (detailTimerRef.current) {
      clearTimeout(detailTimerRef.current);
      detailTimerRef.current = null;
    }
  };

  const openDetailLater = () => {
    if (!tagDetailRoleKey) return;
    clearDetailTimer();
    detailTimerRef.current = setTimeout(
      () => setIsDetailOpen(true),
      DETAIL_HOVER_DELAY_MS,
    );
  };

  const closeDetail = () => {
    clearDetailTimer();
    setIsDetailOpen(false);
  };

  useEffect(() => clearDetailTimer, []);

  const row = (
    <div
      onMouseEnter={() => {
        onHoverStart?.();
        openDetailLater();
      }}
      onMouseLeave={() => {
        onHoverEnd?.();
        closeDetail();
      }}
      // 드래그를 시작하면 팝오버가 행을 따라다니지 않게 닫는다.
      onMouseDownCapture={closeDetail}
      // 항목 사이 16px 간격(itemGap) 중 위아래 6px씩과 좌우 6px 을 hover 영역에 포함시킨다(항목 사이 4px 남김).
      // 음수 margin 으로 되돌려 실제 배치는 그대로 두고 배경만 넓게 칠한다.
      // w-full 을 주면 폭이 100% 로 고정돼 오른쪽만 6px 덜 칠해지므로 stretch 에 맡긴다.
      className={cn("group flex items-center gap-2 rounded-md -my-1.5 py-1.5 -mx-1.5 px-1.5", {
        "bg-blue-50": isSelected && !item.isStructuralOverride && !item.autoAdded,
        "bg-green-50 border border-dashed border-green-300": item.isStructuralOverride,
        "bg-neutral-50 border border-dashed border-neutral-300": item.autoAdded,
        "hover:bg-neutral-800/[.045] active:bg-neutral-900/[.09] cursor-pointer select-none":
          !!onClick && !item.isStructuralOverride && !item.autoAdded,
        "hover:bg-green-100 active:bg-green-100 cursor-pointer select-none":
          !!onClick && item.isStructuralOverride,
        "cursor-default": item.autoAdded,
      })}
    >
      <div
        className={cn(
          "bg-slate-200 flex items-center justify-center rounded-[8px] shrink-0 h-[32px] w-[32px]",
          item.isStructuralOverride && "bg-green-200",
          item.autoAdded && "bg-neutral-200",
        )}
      >
        <DimensionIcon
          tagType={item.tagType as TagType}
          isSum={item.isSum}
          isExtendedTag={item.isExtendedTag}
          isDisabled={disabled || !!item.autoAdded}
          colored={colored && !item.autoAdded}
        />
      </div>
      <div
        className={cn(
          "flex justify-between items-center flex-1 py-[4px] min-w-0",
        )}
      >
        <span className={cn(
          "label-1-normal-medium truncate",
          item.isStructuralOverride ? "text-green-700" : "text-[#0A0A0A]",
          item.autoAdded && "text-label-assistive",
        )}>
          {item.tagKoLabel}
        </span>
        {item.isStructuralOverride && (
          <span className="caption-2-medium text-green-600 bg-green-100 px-1.5 py-0.5 rounded flex-shrink-0">
            추가됨
          </span>
        )}
        {item.autoAdded && (
          <span className="caption-2-medium text-label-assistive bg-neutral-100 px-1.5 py-0.5 rounded flex-shrink-0">
            자동추가됨
          </span>
        )}
        {onRemove && (
          <button
            type="button"
            aria-label="태그 삭제"
            className="flex size-[28px] shrink-0 items-center justify-center rounded-[4px] text-label-alternative opacity-0 transition-all duration-200 hover:bg-black/8 hover:text-status-negative active:bg-black/16 group-hover:opacity-100 focus-visible:opacity-100"
            // 행 클릭(하이라이트)과 dnd 센서로 이벤트가 새지 않게 막는다.
            onClick={(e) => {
              e.stopPropagation();
              closeDetail();
              onRemove();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>
    </div>
  );

  const content = (
    <div
      className={cn("flex flex-row min-w-0 flex-1", className)}
      onClick={onClick}
      // ponytail: autoAdded 항목은 부모 래퍼(dnd-kit Mouse/Touch 센서)로 이벤트가 전파되지 않게 막아 드래그를 비활성화한다.
      // 라이브러리가 항목 단위 disabled를 노출하지 않아 여기서 막는 게 최소 변경.
      onMouseDown={item.autoAdded ? (e) => e.stopPropagation() : undefined}
      onTouchStart={item.autoAdded ? (e) => e.stopPropagation() : undefined}
    >
      {depth > 0 && (
        <div className="">
          <DepthIcon />
        </div>
      )}
      <div className="flex flex-col w-full">
        {depth > 0 && <div className="h-[16px]" />}
        {tagDetailRoleKey ? (
          <TreeTagDetailPopover
            item={item}
            roleKey={tagDetailRoleKey}
            open={isDetailOpen}
            onOpenChange={(open) => {
              if (!open) closeDetail();
            }}
            onContentHoverChange={(hovering) =>
              hovering ? setIsDetailOpen(true) : closeDetail()
            }
          >
            {row}
          </TreeTagDetailPopover>
        ) : (
          row
        )}
      </div>
    </div>
  );

  if (item.autoAdded) {
    return (
      <TooltipWrapper label="자동으로 추가된 태그이므로 이동할 수 없습니다.">
        {content}
      </TooltipWrapper>
    );
  }

  return content;
};

export default TreeTagItem;
